import type { OnInit } from '@angular/core';
import type { FormGroup } from '@angular/forms';
import type { TestimonialSettingsFormType } from '@web/widgets/testimonial/type/testimonial-widget.type';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';

import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';

import { ETestimonialLayout, ETestimonialType, EFontStyle, ELineStyle } from '@common/enums';
import { CSS_RESTRICTIONS } from '@common/constants';

import { WebBaseControlTypedSettingsComponent } from '@web/widgets/common/abstract/web-base-control-typed-settings.component';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { ThemeStateService } from '@sites/site-theme/src/state';

@Component({
    selector: 'web-testimonial-settings',
    templateUrl: './testimonial-settings.component.html',
    styleUrls: ['./testimonial-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule],
})
export class TestimonialSettingsComponent
    extends WebBaseControlTypedSettingsComponent<FormGroup<TestimonialSettingsFormType>>
    implements OnInit
{
    private readonly themeState = inject(ThemeStateService);

    @Input() public testimonialLayout: ETestimonialLayout;
    @Input() public buttonText: string;

    @Output() public setButtonSettings: EventEmitter<void> = new EventEmitter();

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public readonly MAX_TESTIMONIALS_IN_ROW = [1, 2, 3];
    public readonly FONT_SIZE_MAX = 36;
    public readonly TESTIMONIALS_TYPES = [
        { label: 'style-1', type: ETestimonialType.noAvatar },
        { label: 'style-2', type: ETestimonialType.avatarTop },
        { label: 'style-3', type: ETestimonialType.avatarLeft },
    ];
    public readonly ETestimonialLayout = ETestimonialLayout;
    public readonly EFontStyle = EFontStyle;
    public readonly lineStyle = ELineStyle;

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }

    public get SIZE_MIN(): number {
        return CSS_RESTRICTIONS.SIZE_MIN;
    }

    public get SIZE_MAX(): number {
        return CSS_RESTRICTIONS.SIZE_MAX;
    }

    public setRowItems(value: number): void {
        if (value === this.rowItems.value) return;
        this.rowItems.patchValue(value);
    }

    public setSpaceBetween(value: number) {
        if (value === this.spaceBetween.value) return;
        this.spaceBetween.patchValue(value);
    }

    public setTestimonialType(value: ETestimonialType) {
        if (value === this.type.value) return;
        this.type.patchValue(value);
    }

    public setFontSize(value: number) {
        if (value === this.fontSize.value) return;
        this.fontSize.patchValue(value);
    }

    public setFontStyle(value: EFontStyle) {
        this.fontStyle.patchValue(value);
    }

    public setRounding(value: number) {
        this.rounding.patchValue(value);
    }

    public setPaddingTop(value: number) {
        this.paddingTop.patchValue(value);
    }

    public setPaddingBottom(value: number) {
        this.paddingBottom.patchValue(value);
    }

    public setPaddingLeft(value: number) {
        this.paddingLeft.patchValue(value);
    }

    public setAllPaddings(value: number): void {
        this.itemForm.patchValue({
            paddingTop: value,
            paddingBottom: value,
            paddingLeft: value,
            paddingRight: value,
        });
    }

    public setPaddingRight(value: number) {
        if (value === this.paddingRight.value) return;
        this.paddingRight.patchValue(value);
    }

    public setButtonColor({ value }: ColorPickerEventPayload): void {
        if (value === this.buttonColor.value) return;
        this.buttonColor.patchValue(value);
    }

    public setLinkColor({ value }: ColorPickerEventPayload): void {
        if (value === this.linkColor.value) return;
        this.linkColor.patchValue(value);
    }

    public setFontColor({ value }: ColorPickerEventPayload): void {
        if (value === this.fontColor.value) return;
        this.fontColor.patchValue(value);
    }

    public setBackgroundColor({ value }: ColorPickerEventPayload): void {
        if (value === this.backgroundColor.value) return;
        this.backgroundColor.patchValue(value);
    }

    public setButtonSettingsOption() {
        this.onSave();
        this.setButtonSettings.emit();
    }

    public setBorderWidth(value: number): void {
        this.borderWidth.setValue(value);
    }

    public setBorderColor({ value }: ColorPickerEventPayload): void {
        this.borderColor.setValue(value);
    }

    public setAutoScroll(value: boolean) {
        this.isAutoScroll.setValue(value);
    }

    public get rowItems(): TestimonialSettingsFormType['rowItems'] {
        return this.itemForm.get('rowItems') as TestimonialSettingsFormType['rowItems'];
    }

    public get spaceBetween(): TestimonialSettingsFormType['spaceBetween'] {
        return this.itemForm.get('spaceBetween') as TestimonialSettingsFormType['spaceBetween'];
    }

    public get isShortText(): TestimonialSettingsFormType['isShortText'] {
        return this.itemForm.get('isShortText') as TestimonialSettingsFormType['isShortText'];
    }

    public get isShowMore(): TestimonialSettingsFormType['isShowMore'] {
        return this.itemForm.get('isShowMore') as TestimonialSettingsFormType['isShowMore'];
    }

    public get buttonColor(): TestimonialSettingsFormType['buttonColor'] {
        return this.itemForm.get('buttonColor') as TestimonialSettingsFormType['buttonColor'];
    }

    public get isAutoScroll(): TestimonialSettingsFormType['isAutoScroll'] {
        return this.itemForm.get('isAutoScroll') as TestimonialSettingsFormType['isAutoScroll'];
    }

    public get type(): TestimonialSettingsFormType['type'] {
        return this.itemForm.get('type') as TestimonialSettingsFormType['type'];
    }

    public get linkText(): TestimonialSettingsFormType['linkText'] {
        return this.itemForm.get('linkText') as TestimonialSettingsFormType['linkText'];
    }

    public get linkColor(): TestimonialSettingsFormType['linkColor'] {
        return this.itemForm.get('linkColor') as TestimonialSettingsFormType['linkColor'];
    }

    public get fontSize(): TestimonialSettingsFormType['fontSize'] {
        return this.itemForm.get('fontSize') as TestimonialSettingsFormType['fontSize'];
    }

    public get fontStyle(): TestimonialSettingsFormType['fontStyle'] {
        return this.itemForm.get('fontStyle') as TestimonialSettingsFormType['fontStyle'];
    }

    public get fontColor(): TestimonialSettingsFormType['fontColor'] {
        return this.itemForm.get('fontColor') as TestimonialSettingsFormType['fontColor'];
    }

    public get backgroundColor(): TestimonialSettingsFormType['backgroundColor'] {
        return this.itemForm.get('backgroundColor') as TestimonialSettingsFormType['backgroundColor'];
    }

    public get isBorder(): TestimonialSettingsFormType['isBorder'] {
        return this.itemForm.get('isBorder') as TestimonialSettingsFormType['isBorder'];
    }

    public get borderWidth(): TestimonialSettingsFormType['borderWidth'] {
        return this.itemForm.get('borderWidth') as TestimonialSettingsFormType['borderWidth'];
    }

    public get borderStyle(): TestimonialSettingsFormType['borderStyle'] {
        return this.itemForm.get('borderStyle') as TestimonialSettingsFormType['borderStyle'];
    }

    public get borderColor(): TestimonialSettingsFormType['borderColor'] {
        return this.itemForm.get('borderColor') as TestimonialSettingsFormType['borderColor'];
    }

    public get isShadow(): TestimonialSettingsFormType['isShadow'] {
        return this.itemForm.get('isShadow') as TestimonialSettingsFormType['isShadow'];
    }

    public get rounding(): TestimonialSettingsFormType['rounding'] {
        return this.itemForm.get('rounding') as TestimonialSettingsFormType['rounding'];
    }

    public get paddingTop(): TestimonialSettingsFormType['paddingTop'] {
        return this.itemForm.get('paddingTop') as TestimonialSettingsFormType['paddingTop'];
    }

    public get paddingBottom(): TestimonialSettingsFormType['paddingBottom'] {
        return this.itemForm.get('paddingBottom') as TestimonialSettingsFormType['paddingBottom'];
    }

    public get paddingLeft(): TestimonialSettingsFormType['paddingLeft'] {
        return this.itemForm.get('paddingLeft') as TestimonialSettingsFormType['paddingLeft'];
    }

    public get paddingRight(): TestimonialSettingsFormType['paddingRight'] {
        return this.itemForm.get('paddingRight') as TestimonialSettingsFormType['paddingRight'];
    }
}
